"use strict";
/**
 * Copyright (c) 2024 BrightSpike
 * All rights reserved.
 *
 * This source code is unlicensed.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * For detailed license information, please contact Brightspike at (info@brightspike.io).
 * @author Austin Weidler
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.GameManager = void 0;
const PlayerManager_1 = require("./PlayerManager");
const webSocket_1 = require("rxjs/webSocket");
const rxjs_1 = require("rxjs");
const interfaces_1 = require("../../../shared/interfaces");
const Serializable_1 = require("../../../shared/Serializable");
const lodash_1 = __importDefault(require("lodash"));
const config_json_1 = __importDefault(require("../../../shared/config.json"));
const Global_1 = require("../Global");
const AdManager_1 = require("./AdManager");
const PurchaseManager_1 = require("./PurchaseManager");
class GameManager {
    constructor() {
        this.since = null;
        this.seq = null;
        this.pending = [];
        this.disconnecter = () => {
            this.disconnected();
        };
        this.visibilityChange = () => {
            // If we are out of focus, take us back to the homepage, because we aren't paying attention anyway...
            if (document.visibilityState === 'hidden') {
                this.ws.complete();
            }
            else {
                this.connect();
            }
        };
        this.connected$ = new rxjs_1.BehaviorSubject(false);
        this.reconnect = 0;
        this.reconnect$ = new rxjs_1.Subject();
        this.crash$ = new rxjs_1.Subject();
        this._isPlaying = false;
        this._inited = false;
        this.subs = new rxjs_1.Subscription();
        this.destroyed = false;
        this.playerManager = new PlayerManager_1.PlayerManager(this);
        this.adManager = new AdManager_1.AdManager(this);
        this.purchaseManager = new PurchaseManager_1.PurchaseManager(this);
        this.userChange$ = new rxjs_1.BehaviorSubject(null);
        this.langChange$ = new rxjs_1.Subject();
        this.room$ = new rxjs_1.BehaviorSubject(null);
        this.stat$ = new rxjs_1.BehaviorSubject(null);
        this.rank$ = new rxjs_1.BehaviorSubject(null);
        this.roomStream = {
            round$: new rxjs_1.BehaviorSubject(null),
            playerenter$: new rxjs_1.Subject(),
            playerleave$: new rxjs_1.Subject(),
            ready$: new rxjs_1.Subject(),
            draw$: new rxjs_1.Subject(),
            submit$: new rxjs_1.Subject(),
            roundfinish$: new rxjs_1.Subject(),
            roundcancel$: new rxjs_1.Subject(),
            complete$: new rxjs_1.Subject(),
            roomupdate$: new rxjs_1.Subject(),
            guess$: new rxjs_1.Subject()
        };
        // Save lang to player if it is changed.
        this.subs.add(this.langChange$.subscribe((other) => __awaiter(this, void 0, void 0, function* () {
            this.send(interfaces_1.SocketAction.ChangeLanguage, other);
        })));
        // In case we do not recover in time...
        let wasrunningroom = false;
        this.subs.add(this.connected$.subscribe((is) => __awaiter(this, void 0, void 0, function* () {
            if (is) { // online
                if (this.reconnect) {
                    (0, rxjs_1.firstValueFrom)(this.playerManager.playerData$.pipe((0, rxjs_1.skip)(1))).then(() => {
                        this.reconnect$.next(this.reconnect);
                    });
                }
                if (wasrunningroom) {
                    (0, rxjs_1.firstValueFrom)(this.room$.pipe((0, rxjs_1.skip)(1))).then((nextroom) => {
                        if (!nextroom) { // we were disconnected from the room for too long
                            this.crash$.next(true);
                        }
                    });
                }
                wasrunningroom = false;
            }
            else { // power cut
                wasrunningroom = (this.room$.value && !this.room$.value.open && !this.room$.value.complete);
                this.reconnect++;
            }
        })));
        this.subs.add(this.connected$.pipe((0, rxjs_1.first)(o => o)).subscribe(() => {
            // Check our config refresh
            this.send(interfaces_1.SocketAction.Config);
        }));
        // Disconnect from client side
        window.addEventListener('offline', this.disconnecter);
        document.addEventListener("visibilitychange", this.visibilityChange);
        // A way of manually killing the connection for testing
        if ((0, Global_1.isDebug)()) {
            document.body.addEventListener('keydown', (e) => {
                if (e.key === 'e') {
                    this.disconnected();
                }
            });
        }
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this._inited) {
                yield this.playerManager.init();
                this.connect();
                this._inited = true;
            }
            return this;
        });
    }
    connect() {
        clearTimeout(this.timer);
        if (!this.destroyed && document.visibilityState === 'visible') {
            this.ws = (0, webSocket_1.webSocket)({
                url: process.env.SOCKET_ROOT,
                deserializer: e => {
                    // Dirty test if this data is actually a json object...
                    if (e.data[0] === '{' || e.data[0] === '[') {
                        return JSON.parse(e.data);
                    }
                    return e.data;
                },
                serializer: (value) => {
                    if (value === null || value === void 0 ? void 0 : value.action) {
                        return JSON.stringify(value);
                    }
                    return value;
                },
                openObserver: {
                    next: (value) => __awaiter(this, void 0, void 0, function* () {
                        if (!this.connected$.value) {
                            this.connected$.next(true);
                        }
                        yield this.catchup();
                    }),
                    error: (err) => { console.error(err); }
                }
            });
            this.wssub = this.ws.subscribe({
                next: (data) => {
                    if (data === config_json_1.default.pingcode) {
                        this.submit(config_json_1.default.pingcode);
                    }
                    else {
                        this.route(data);
                    }
                },
                error: (err) => {
                    this.disconnected();
                },
                complete: () => {
                    this.disconnected();
                }
            });
        }
    }
    disconnected() {
        var _a, _b, _c;
        if (!this.since) {
            this.since = Date.now();
        }
        // If we disconnect while in the waiting room, then don't reconnect to it (someone may have joined in the meantime)
        if ((((_a = this.room$.value) === null || _a === void 0 ? void 0 : _a.open) || ((_b = this.room$.value) === null || _b === void 0 ? void 0 : _b.complete)) && (0, Global_1.isPublicRoom)(this.room$.value)) {
            this.clearRoom();
        }
        (_c = this.wssub) === null || _c === void 0 ? void 0 : _c.unsubscribe();
        if (this.connected$.value) {
            this.connected$.next(false);
            this.connect();
        }
        else {
            this.timer = setTimeout(() => {
                this.connect();
            }, config_json_1.default.reconnectinterval);
        }
    }
    catchup() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.connected$.value) {
                yield this.playerManager.join((_a = this.seq) !== null && _a !== void 0 ? _a : this.since);
                this.since = null;
                if (this.pending.length) {
                    this.pending.forEach((item) => {
                        this.submit(item);
                    });
                    this.pending = [];
                }
            }
        });
    }
    checkStat(stat, day = null) {
        this.send(interfaces_1.SocketAction.Stat, { stat, day });
    }
    notifySkip() {
        this.send(interfaces_1.SocketAction.Skip);
    }
    notifyReady(atterm = null) {
        this.send(interfaces_1.SocketAction.Ready, atterm);
    }
    guess(item) {
        this.send(interfaces_1.SocketAction.Guess, item);
    }
    enterRoom(type = interfaces_1.GameType.Battle, rkey = null) {
        this.clearRoom();
        this.send(interfaces_1.SocketAction.EnterRoom, { rkey, type });
    }
    leaveRoom() {
        this.clearRoom();
        this.send(interfaces_1.SocketAction.LeaveRoom);
    }
    checkUser(user) {
        if ((user === null || user === void 0 ? void 0 : user.trim().length) && (user === null || user === void 0 ? void 0 : user.trim()) !== this.playerManager.data.user) {
            this.send(interfaces_1.SocketAction.CheckUser, user.trim());
            return true;
        }
        return false;
    }
    changeUser(user) {
        if ((user === null || user === void 0 ? void 0 : user.trim().length)
            && (user === null || user === void 0 ? void 0 : user.trim()) !== this.playerManager.data.user
            && this.userChange$.value === interfaces_1.UserValid.Available) {
            this.send(interfaces_1.SocketAction.ChangeUser, user.trim());
            return true;
        }
        else {
            this.userChange$.next(null);
        }
        return false;
    }
    submitDraw(encoded) {
        // These are many rapid calls, we cannot keep json encoded/decoding, bypass with encoded strings
        this.submit(encoded);
    }
    send(action, payload = null) {
        if (payload instanceof Serializable_1.Serializable) {
            payload = payload.toJSON();
        }
        const data = {
            action: action,
            payload: payload
        };
        this.submit(data);
    }
    submit(info) {
        var _a;
        if (this.connected$.value) {
            (_a = this.ws) === null || _a === void 0 ? void 0 : _a.next(info);
        }
        else {
            // Will try to submit once connected again...
            this.pending.push(info);
        }
    }
    get opponents() {
        const room = this.room$.value;
        if (room) {
            return lodash_1.default.values(room.players).filter((player) => player.pkey !== this.playerManager.pkey);
        }
        return null;
    }
    opponent(pkey) {
        var _a;
        return ((_a = this.room$.value) === null || _a === void 0 ? void 0 : _a.players[pkey]) || null;
    }
    score(player = null) {
        var _a, _b;
        return (_b = (_a = this.room$.value) === null || _a === void 0 ? void 0 : _a.players[player || this.playerManager.pkey]) === null || _b === void 0 ? void 0 : _b.score;
    }
    route(data) {
        var _a;
        if (!data.action && data.pkey) {
            // Assume is a rapid draw command for the room stream, we are omitting action and rkey for just network speed
            const d = data;
            if (d.id) {
                this.seq = d.id;
            }
            return this.roomStream.submit$.next({
                pkey: d.pkey,
                state: parseInt(d.state),
                labels: lodash_1.default.trimEnd(d.labels, ',').split(',').filter(l => l),
                pt: !d.pt || d.pt === 'D' ? null : d.pt.substring(config_json_1.default.pointdigits).split(',').map((p) => parseFloat(p)),
                stroke: !d.pt || d.pt === 'D' ? null : parseInt(d.pt.substring(0, config_json_1.default.pointdigits))
            });
        }
        switch (data.action) {
            case interfaces_1.SocketAction.Joined:
            case interfaces_1.SocketAction.ChangedPlayer:
                if (data.payload) {
                    this.playerManager.setData((data.payload)).then();
                }
                break;
            case interfaces_1.SocketAction.CheckedUser:
                this.userChange$.next((data.payload));
                break;
            case interfaces_1.SocketAction.Room:
                // Room Status
                if (data.payload) {
                    this.room$.next((data.payload));
                }
                else {
                    this.clearRoom();
                }
                break;
            case interfaces_1.SocketAction.Stat:
                this.stat$.next((data.payload));
                break;
            case interfaces_1.SocketAction.RoomDone:
                const rank = (data.payload);
                this.rank$.next(rank);
                break;
            case interfaces_1.SocketAction.RoomStream:
                // Room Update
                const pl = (data.payload);
                if (pl.rkey && ((_a = this.room$.value) === null || _a === void 0 ? void 0 : _a.rkey.toString()) === pl.rkey.toString()) {
                    this.streamRoute(pl);
                }
                break;
            case interfaces_1.SocketAction.Config:
                // Update local config values from server (handy for not needing to push new updates to stores
                const info = (data.payload);
                for (let k in info) {
                    if (k in config_json_1.default && info[k]) {
                        config_json_1.default[k] = info[k];
                    }
                }
                break;
        }
    }
    maproom(roomStream) {
        const rom = JSON.parse(roomStream.room);
        if (rom) {
            this.room$.next(rom);
        }
        return rom || null;
    }
    streamRoute(roomStream) {
        var _a, _b;
        if (roomStream.id) {
            this.seq = roomStream.id;
        }
        let val = null;
        switch (roomStream.action) {
            case interfaces_1.StreamAction.Complete:
                // Update our room state
                val = this.maproom(roomStream);
                break;
            case interfaces_1.StreamAction.Guess:
                val = {
                    pkey: roomStream.pkey,
                    guess: parseInt(roomStream.guess),
                    label: roomStream.labels
                };
                break;
            case interfaces_1.StreamAction.Round:
                // Update our room state
                this.maproom(roomStream);
                // Get round data
                val = {
                    start: parseInt(roomStream.start),
                    time: parseInt(roomStream.time),
                    type: parseInt(roomStream.type),
                    label: roomStream.label,
                    state: parseInt(roomStream.state),
                    guesses: roomStream.guesses ? roomStream.guesses.split(',') : [],
                    drawing: roomStream.drawing ? JSON.parse(roomStream.drawing) : null
                };
                break;
            case interfaces_1.StreamAction.Draw:
                val = {
                    state: parseInt(roomStream.state)
                };
                break;
            case interfaces_1.StreamAction.Ready:
            case interfaces_1.StreamAction.RoundCancel:
                val = parseInt(roomStream.state);
                break;
            case interfaces_1.StreamAction.RoomUpdate:
                val = this.maproom(roomStream);
                break;
            case interfaces_1.StreamAction.PlayerEnter:
                const rr = this.maproom(roomStream);
                val = (rr === null || rr === void 0 ? void 0 : rr.players[roomStream.pkey]) || null;
                break;
            case interfaces_1.StreamAction.PlayerLeave:
                val = ((_a = this.room$.value) === null || _a === void 0 ? void 0 : _a.players[roomStream.pkey]) || null;
                this.maproom(roomStream);
                break;
            case interfaces_1.StreamAction.RoundFinish:
                val = {
                    pkey: roomStream.pkey,
                    state: parseInt(roomStream.state),
                    labels: lodash_1.default.trimEnd(roomStream.labels, ',').split(','),
                    pt: null,
                    stroke: null
                };
                break;
        }
        if (val) {
            (_b = this.roomStream[roomStream.action + '$']) === null || _b === void 0 ? void 0 : _b.next(val);
        }
    }
    clearRoom() {
        this.seq = null;
        if (this.roomStream.round$.value) {
            this.roomStream.round$.next(null);
        }
        this.room$.next(null);
    }
    destroy() {
        var _a;
        this.subs.unsubscribe();
        (_a = this.wssub) === null || _a === void 0 ? void 0 : _a.unsubscribe();
        window.removeEventListener('offline', this.disconnecter);
        document.removeEventListener("visibilitychange", this.visibilityChange);
        this.adManager.destroy();
        this.destroyed = true;
    }
    get isPlaying() {
        return this._isPlaying;
    }
    startPlay() {
        var _a;
        if (!this.isPlaying) {
            this._isPlaying = true;
            (_a = (0, Global_1.poki)()) === null || _a === void 0 ? void 0 : _a.gameplayStart();
        }
    }
    stopPlay() {
        var _a;
        if (this.isPlaying) {
            this._isPlaying = false;
            (_a = (0, Global_1.poki)()) === null || _a === void 0 ? void 0 : _a.gameplayStop();
        }
    }
}
exports.GameManager = GameManager;
